import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { formatQuantity } from "../lib/utils";
import type { Ingredient, ShoppingListItem, StoreSection } from "../types";

interface ByPlanResp {
  list: { id: string; generatedAt: number } | null;
  items?: (ShoppingListItem & { ingredient: Ingredient })[];
  sections: StoreSection[];
}

export default function ShoppingListPage() {
  const { id = "", storeId = "" } = useParams();
  const qc = useQueryClient();

  const dataQ = useQuery({
    queryKey: ["shopping-list", id, storeId],
    queryFn: () => api.get<ByPlanResp>(`/api/shopping-lists/by-plan?mealPlanId=${id}&storeId=${storeId}`),
  });

  const generate = useMutation({
    mutationFn: () => api.post(`/api/shopping-lists/by-plan?mealPlanId=${id}&storeId=${storeId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shopping-list", id, storeId] }),
  });

  const toggle = useMutation({
    mutationFn: ({ itemId, checked }: { itemId: string; checked: boolean }) =>
      api.patch(`/api/shopping-lists/items/${itemId}`, { checked }),
    onMutate: ({ itemId, checked }) => {
      const prev = qc.getQueryData<ByPlanResp>(["shopping-list", id, storeId]);
      if (prev?.items) {
        qc.setQueryData<ByPlanResp>(["shopping-list", id, storeId], {
          ...prev,
          items: prev.items.map((it) => (it.id === itemId ? { ...it, checked } : it)),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["shopping-list", id, storeId], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["shopping-list", id, storeId] });
      qc.invalidateQueries({ queryKey: ["fridge"] });
    },
  });

  const sectionOrder = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of dataQ.data?.sections ?? []) m.set(s.id, s.sortOrder);
    return m;
  }, [dataQ.data?.sections]);

  const grouped = useMemo(() => {
    const items = dataQ.data?.items ?? [];
    const groups = new Map<string, { sectionId: string | null; name: string; order: number; items: typeof items }>();
    const sectionName = new Map<string, string>();
    for (const s of dataQ.data?.sections ?? []) sectionName.set(s.id, s.name);
    for (const it of items) {
      const key = it.sectionId ?? "_none";
      if (!groups.has(key)) {
        groups.set(key, {
          sectionId: it.sectionId,
          name: it.sectionId ? sectionName.get(it.sectionId) ?? "Bez sekce" : "Bez sekce",
          order: it.sectionId ? sectionOrder.get(it.sectionId) ?? 999 : 9999,
          items: [],
        });
      }
      groups.get(key)!.items.push(it);
    }
    return Array.from(groups.values()).sort((a, b) => a.order - b.order);
  }, [dataQ.data, sectionOrder]);

  return (
    <div>
      <Link to={`/jidelnicky/${id}`} className="text-sm text-slate-500 hover:underline">
        ← Zpět na jídelníček
      </Link>
      <PageHeader
        icon="🛒"
        title="Nákupní seznam"
        description="Seznam je seřazený podle sekcí obchodu. Zaškrtnutím se položka přidá do lednice."
        actions={
          <button className="btn-primary flex items-center gap-1" onClick={() => generate.mutate()} disabled={generate.isPending}>
            <span aria-hidden>🔄</span>
            <span>{dataQ.data?.list ? "Vygenerovat znovu" : "Vygenerovat seznam"}</span>
          </button>
        }
      />

      {!dataQ.data?.list ? (
        <div className="card card-body text-center text-slate-500">
          {dataQ.isLoading
            ? "Načítám…"
            : "Pro tento jídelníček a obchod ještě nebyl seznam vygenerován. Klikněte na „Vygenerovat seznam“."}
        </div>
      ) : grouped.length === 0 ? (
        <div className="card card-body text-center text-slate-500">
          Seznam je prázdný – buď jídelníček neobsahuje recepty, nebo vše už máte v lednici.
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map((g) => (
            <div key={g.sectionId ?? "_none"} className="card">
              <div className="px-4 py-2 border-b bg-slate-50 font-semibold">{g.name}</div>
              <ul className="divide-y">
                {g.items.map((it) => {
                  const onScale = it.ingredient.packageSize === 0;
                  return (
                    <li key={it.id} className="px-4 py-3 flex items-center gap-3">
                      <input
                        type="checkbox"
                        className="w-5 h-5"
                        checked={!!it.checked}
                        onChange={(e) => toggle.mutate({ itemId: it.id, checked: e.target.checked })}
                      />
                      <div className={"flex-1 " + (it.checked ? "line-through text-slate-400" : "")}>
                        <div className="font-medium">{it.ingredient.name}</div>
                        <div className="text-xs text-slate-500">
                          potřeba {formatQuantity(it.requiredQuantity, it.ingredient.unit)}
                          {onScale ? (
                            <> · <span className="text-amber-700">na váhu</span></>
                          ) : (
                            <> · balení {it.ingredient.packageSize} {it.ingredient.unit}</>
                          )}
                        </div>
                      </div>
                      <div className="font-semibold text-brand-700 text-right">
                        {onScale ? (
                          <>
                            {formatQuantity(it.requiredQuantity, it.ingredient.unit)}
                          </>
                        ) : (
                          <>
                            {it.packageCount}× <span className="text-xs font-normal text-slate-500">balení</span>
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
